interface CodePanelProps {
  filename: string;
  entries: ReadonlyArray<readonly [string, string]>;
  rows: ReadonlyArray<readonly [string, string]>;
}

/** The About section's signature object: a double-framed mock editor pane with mono "code". */
export function CodePanel({ filename, entries, rows }: CodePanelProps) {
  return (
    <div className="relative w-full max-w-lg lg:max-w-lg">
      <div
        aria-hidden="true"
        className="absolute -inset-8 -z-10 rounded-full bg-blue-500/10 blur-[48px]"
      />
      <div className="border-hairline bg-raised shadow-panel overflow-hidden rounded-card border p-2 backdrop-blur-xl">
        <div className="border-hairline rounded-inset border bg-page p-6 sm:p-8">
          <div className="mb-8 flex items-center justify-between">
            <div className="flex gap-2" aria-hidden="true">
              <span className="size-2.5 rounded-full bg-rose-400" />
              <span className="size-2.5 rounded-full bg-amber-400" />
              <span className="size-2.5 rounded-full bg-emerald-300" />
            </div>
            <span className="bg-subtle text-muted rounded-full px-3 py-1 font-mono text-[0.68rem]">
              {filename}
            </span>
          </div>

          <div className="font-mono text-sm leading-[1.85]">
            <p>
              <span className="text-blue-400">const</span>{" "}
              <span className="text-cyan-300">developer</span>{" "}
              <span className="text-dim">=</span>{" "}
              <span className="text-body">{"{"}</span>
            </p>
            {entries.map(([key, value]) => (
              <p key={key} className="pl-5">
                <span className="text-code-key">{key}:</span>{" "}
                <span className="text-code-string">{`"${value}"`}</span>,
              </p>
            ))}
            <p>
              <span className="text-body">{"}"}</span>;
            </p>
          </div>

          {rows.length ? (
            <div className="mt-9 grid gap-3">
              {rows.map(([label, value], index) => (
                <StatRow
                  key={label}
                  index={index + 1}
                  label={label}
                  value={value}
                />
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function StatRow({
  index,
  label,
  value,
}: {
  index: number;
  label: string;
  value: string;
}) {
  return (
    <div className="group border-hairline bg-faint hover:border-accent-line ease-standard flex items-center justify-between gap-4 rounded-2xl border px-4 py-3.5 transition duration-200 hover:-translate-y-0.5">
      <div className="flex min-w-0 items-center gap-3">
        <span className="bg-subtle text-muted group-hover:bg-accent-surface group-hover:text-accent-strong ease-standard grid size-9 shrink-0 place-items-center rounded-xl font-mono text-xs font-semibold transition duration-200">
          {String(index).padStart(2, "0")}
        </span>
        <span className="text-muted truncate text-sm">{label}</span>
      </div>
      <span className="text-strong text-right text-sm font-semibold">
        {value}
      </span>
    </div>
  );
}
